import React, { useEffect, useState } from "react";

import {
  filterOptionsWithSynonyms,
  formatNumericCurrency,
  useAddFieldCropPractice,
  useGetFarms,
  useGetFieldPractices,
  useGetPracticeSeedings,
  useTranslation,
  useValidateCropPeriods,
} from "utils";

import { CurrencyCode } from "consts";

import { ACCOUNTING_METHODOLOGY, CROP_TYPE_FCPO, FieldCropRecommendation, PRACTICE_SLUG } from "models";

import { AlertBanner, Button, Divider, Flex, Form, Icon, OptGroup, Option, Radio, RadioChangeEvent, RadioPill, RadioPillGroup, Select } from "components";

export type CropRecommendationCardFormProps = {
  recommendations: FieldCropRecommendation[];
  cropType: CROP_TYPE_FCPO;
  harvestEndDate: string;
  seedingStartDate: string;
  fieldId: string;
  onClose: () => void;
  year?: number;
  simulationId?: string;
  fieldSize?: number;
};

export const CropRecommendationCardForm = ({
  recommendations,
  cropType,
  harvestEndDate,
  seedingStartDate,
  onClose,
  year,
  fieldId,
  simulationId,
  fieldSize,
}: CropRecommendationCardFormProps) => {
  const { t } = useTranslation(["fieldTimeline", "practices"]);
  const seedingPracticesQuery = useGetPracticeSeedings({ enabled: true });
  const [form] = Form.useForm();
  const [selectedCropId, setSelectedCropId] = useState<string | undefined>();
  const [selectionResiduePayout, setSelectionResiduePayout] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isTillageAndSeedingBundleActive, setIsTillageAndSeedingBundleActive] = useState(false);
  const { mutate: addFieldCropPractice } = useAddFieldCropPractice({ fieldId, simulationId });
  const { data: fieldPractices } = useGetFieldPractices({ fieldId: fieldId || "", inView: true, simulationId });
  const { validatePeriods } = useValidateCropPeriods();

  const { currentFarm } = useGetFarms();
  const isInventoryProgram = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  const selectedRecommendation = recommendations.find((recommendation) => recommendation.cropId === selectedCropId);

  const handleSave = async (formData: { cropId: string; hasResidue?: boolean; seedingPracticeType?: string }) => {
    if (!fieldId || !fieldPractices) return;

    setIsLoading(true);

    const newCropFromRecommendation = {
      cropId: formData.cropId,
      cropType,
      harvestEndDate: harvestEndDate,
      seedingStartDate: seedingStartDate,
      seedingPracticeType: formData.seedingPracticeType ?? null,
      hasResidue: !!formData.hasResidue,
      yieldTonsPerHa: null,
      seedingVerificationStatus: null,
    };

    const periodValidationErrors = validatePeriods(
      {
        ...newCropFromRecommendation,
        id: "",
        cropName: selectedRecommendation ? t(`practices:${PRACTICE_SLUG.CROPS}.${selectedRecommendation.translationSlug}`) : "",
        isConfirmed: false,
        payout: 0,
        translationSlug: "",
        harvestEndDate: harvestEndDate,
        seedingEndDate: "",
        harvestStartDate: "",
        seedingStartDate: seedingStartDate,
        seedingVerificationStatus: null,
      },
      fieldPractices.crops
    );
    if (periodValidationErrors.length > 0) return;

    addFieldCropPractice({
      requestBody: {
        crop: newCropFromRecommendation,
        practices: [],
        hasFertilizerManagement: null,
      },
    });

    setIsLoading(false);
    onClose();
  };

  const onCropSelectionChanged = (e: RadioChangeEvent) => {
    form.resetFields();
    form.setFieldsValue({ cropId: e.target.value });
    setSelectedCropId(e.target.value);
  };

  useEffect(() => {
    if (!selectedCropId || !selectedRecommendation) return;
    if (typeof selectedRecommendation.payoutDifference.withResidue !== "number" || typeof selectedRecommendation.payoutDifference.withoutResidue !== "number") {
      setSelectionResiduePayout(0);
      return;
    }

    const residuePayoutPotential = selectedRecommendation.payoutDifference.withResidue - selectedRecommendation.payoutDifference.withoutResidue;
    setSelectionResiduePayout(residuePayoutPotential);
  }, [recommendations, selectedCropId, selectedRecommendation]);

  useEffect(() => {
    setIsTillageAndSeedingBundleActive(!!fieldPractices?.availablePracticeBundles.find((practiceBundle) => practiceBundle.slug === "tillage")?.active);
  }, [fieldPractices]);

  return (
    <Form
      data-testid="crop-recommendation-card-form"
      form={form}
      onFinish={handleSave}
      name="crop-recommendation-card-form"
      className="crop-recommendation-card-form"
    >
      <Flex className="crop-recommendation-card-form header" justify="space-between">
        <Flex gap=".5rem" align="center" justify="center">
          <Icon className="bulb-icon" iconName="tabler/bulb" />
          <h5>{t("cropCard.recommendations.form.title")}</h5>
        </Flex>
        <Icon className="pointer" iconName="tabler/x" onClick={onClose} />
      </Flex>
      {year && <p className="paragraph-m-bold margin-top-200 ">{t("cropCard.recommendations.form.subtitle", { year })}</p>}
      <p className="paragraph-m margin-top-125">{t("cropCard.recommendations.form.selectLabel")}</p>
      <Form.Item name="cropId" className="margin-top-125" rules={[{ required: true, message: t("addEditCrop.selectionRequiredErrorMessage") }]}>
        <RadioPillGroup onChange={onCropSelectionChanged} value={selectedCropId}>
          {recommendations.map((recommendedCrop) => (
            <RadioPill
              dataTestId={`crop-recommendation-card-form-radio-group-${recommendedCrop.cropId}`}
              className="recommendation-card-form-crop-option"
              additionalInfo={
                isInventoryProgram
                  ? undefined
                  : `(${t("addEditCrop.avgPayout")} ${getPayoutString(
                      recommendedCrop.payoutDifference.withoutResidue,
                      currentFarm?.carbonProgram?.currency,
                      fieldSize,
                      simulationId
                    )})`
              }
              key={recommendedCrop.cropId}
              label={t(`practices:${PRACTICE_SLUG.CROPS}.${recommendedCrop.translationSlug}`)}
              value={recommendedCrop.cropId}
            />
          ))}
        </RadioPillGroup>
      </Form.Item>
      {selectedRecommendation?.hasResidue && selectedCropId ? (
        <>
          <Divider className="margin-top-125 margin-bottom-100" />
          <p className="residue-label">{t("addEditCrop.residueLeft")}</p>
          {selectionResiduePayout !== 0 && (
            <AlertBanner
              dataTestId="crop-recommendation-card-form-residue-banner"
              className="margin-bottom-100"
              description={selectionResiduePayout > 0 ? t("addEditCrop.leaveResidueInfo") : t("addEditCrop.removeResidueInfo")}
              type="info"
              iconName="tabler/bulb"
            />
          )}
          <Form.Item name="hasResidue" initialValue={undefined} rules={[{ required: true, message: t("addEditCrop.selectionRequiredErrorMessage") }]}>
            <Radio.Group
              data-testid="crop-recommendation-card-form-residue-radio-group"
              className="recommendation-card-form radio-button-group"
              optionType="button"
              buttonStyle="solid"
              options={[
                {
                  value: true,
                  label: (
                    <Flex vertical justify="space-around">
                      <span>{t("addEditCrop.yes")}</span>
                      {typeof selectionResiduePayout === "number" && selectionResiduePayout !== 0 && !isInventoryProgram && (
                        <span className="paragraph-s grey-text" data-testid="crop-recommendation-card-form-residue-payout-option-yes">
                          {t("addEditCrop.avgPayout")} {getPayoutString(selectionResiduePayout, currentFarm?.carbonProgram?.currency, fieldSize, simulationId)}
                        </span>
                      )}
                    </Flex>
                  ),
                },
                {
                  value: false,
                  label: (
                    <Flex vertical justify="space-around">
                      <span>{t("addEditCrop.no")}</span>
                      {typeof selectionResiduePayout === "number" && selectionResiduePayout !== 0 && !isInventoryProgram && (
                        <span className="paragraph-s grey-text">{formatNumericCurrency(0, { decimals: 0 })}</span>
                      )}
                    </Flex>
                  ),
                },
              ]}
            />
          </Form.Item>
        </>
      ) : null}
      {cropType !== CROP_TYPE_FCPO.UNDERSOWING && selectedCropId && isTillageAndSeedingBundleActive ? (
        <>
          <Divider className="margin-top-125 margin-bottom-100" />
          <p className="residue-label">{t("cropCard.recommendations.form.seedingTypeLabel")}</p>
          <div>
            {/* @ts-expect-error need to convert to tsx */}
            <Select
              dataTestId="crop-recommendation-card-form-seeding-type-selection"
              filterOption={filterOptionsWithSynonyms}
              label={t("addEditCrop.seedingType")}
              optionFilterProp="children"
              name="seedingPracticeType"
              rules={[{ message: t("addEditCrop.selectionRequiredErrorMessage"), required: true }]}
              showSearch
            >
              <OptGroup>
                {seedingPracticesQuery.data &&
                  seedingPracticesQuery.data.map((option, index) => (
                    <Option data-testid={`crop-selection-option-${index}`} key={option.id} value={option.id}>
                      {option.name}
                    </Option>
                  ))}
              </OptGroup>
            </Select>
          </div>
        </>
      ) : null}
      <Flex justify="flex-end" align="center" gap="1rem" className="margin-top-200">
        <Button
          dataTestId="crop-recommendation-card-form-save-button"
          className="crop-recommendation-card-form save-button"
          type="primary"
          size="small"
          htmlType="submit"
          loading={isLoading}
          disabled={isLoading}
        >
          {t("addEditCrop.save")}
        </Button>
      </Flex>
    </Form>
  );
};

const formatPayoutValue = (value: number, currency?: CurrencyCode): string => {
  if (!value) return formatNumericCurrency(0, { currency, decimals: 0 });
  return formatNumericCurrency(value, { currency, decimals: 0, signDisplay: "always" });
};

const getPayoutString = (payout: number, currency?: CurrencyCode, fieldSize?: number, simulationId?: string) => {
  if (simulationId && fieldSize) {
    const payoutPerHa = payout / fieldSize;
    return `${formatPayoutValue(payoutPerHa, currency)}/ha`;
  }

  return formatPayoutValue(payout, currency);
};
